/**
 * store-stock-checker.js
 * Fetches per-location inventory via Shopify's internal pickup availability endpoint.
 * This is the same endpoint Shopify's native pickup availability block uses.
 * Endpoint: /api/predictive-search/pickup_availability?variant_id=[id]
 * Fallback:  /?section_id=pickup-availability (section rendering API)
 */

const StoreStockChecker = (() => {
  const MODAL_ID = 'store-stock-modal';
  const LIST_ID  = 'store-stock-list';
  const LABEL_ID = 'store-stock-variant-label';
  const cache    = {};

  function getConfig() { return window.storeStockConfig || {}; }
  function getModal()  { return document.getElementById(MODAL_ID); }

  function open() {
    const modal = getModal();
    if (!modal) return;
    modal.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
    modal.addEventListener('click', handleBackdropClick);
    document.addEventListener('keydown', handleKeydown);
    fetchAndRender();
  }

  function close() {
    const modal = getModal();
    if (!modal) return;
    modal.setAttribute('hidden', '');
    document.body.style.overflow = '';
    modal.removeEventListener('click', handleBackdropClick);
    document.removeEventListener('keydown', handleKeydown);
  }

  function handleBackdropClick(e) { if (e.target === getModal()) close(); }
  function handleKeydown(e)       { if (e.key === 'Escape') close(); }

  function getStatusInfo(available) {
    const low = getConfig().lowThreshold || 3;
    if (!available || available <= 0) {
      return { label: 'Out of Stock', dotClass: 'store-stock-checker__dot--out' };
    } else if (available <= low) {
      return { label: 'Low in Stock', dotClass: 'store-stock-checker__dot--low' };
    }
    return { label: 'In Stock', dotClass: 'store-stock-checker__dot--in-stock' };
  }

  function showLoading() {
    const list = document.getElementById(LIST_ID);
    if (list) list.innerHTML = '<div class="store-stock-checker__loading">Checking stock&hellip;</div>';
  }

  function showError(msg) {
    const list = document.getElementById(LIST_ID);
    if (list) list.innerHTML = '<div class="store-stock-checker__loading">' + (msg || 'Unable to load stock. Please try again.') + '</div>';
  }

  function render(locations) {
    const list     = document.getElementById(LIST_ID);
    const label    = document.getElementById(LABEL_ID);
    const excluded = getConfig().excludedLocations || [];
    if (!list) return;

    const filtered = locations.filter(function(loc) {
      return !excluded.includes(loc.name || loc.location_name);
    });

    if (label) {
      const title = getCurrentVariantTitle();
      label.textContent = title ? 'Selected: ' + title : '';
    }

    if (!filtered.length) {
      showError('No stock information available.');
      return;
    }

    list.innerHTML = filtered.map(function(loc) {
      const name      = loc.name || loc.location_name || 'Unknown';
      const available = typeof loc.available === 'number' ? loc.available : (loc.pick_up_enabled ? 1 : 0);
      const status    = getStatusInfo(available);
      return '<div class="store-stock-checker__row">' +
        '<span class="store-stock-checker__location">' + escapeHtml(name) + '</span>' +
        '<span class="store-stock-checker__status">' +
          status.label +
          '<span class="store-stock-checker__dot ' + status.dotClass + '" aria-hidden="true"></span>' +
        '</span>' +
      '</div>';
    }).join('');
  }

  function fetchAndRender() {
    const variantId = getConfig().currentVariantId;
    if (!variantId) { showError(); return; }
    if (cache[variantId]) { render(cache[variantId]); return; }

    showLoading();

    // Try the storefront pickup availability endpoint first
    fetch('/api/2023-04/pickup_availability?variant_id=' + variantId, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
    .then(function(res) {
      console.log('[StoreStock] pickup_availability status:', res.status);
      if (!res.ok) throw new Error('pickup_availability ' + res.status);
      return res.json();
    })
    .then(function(data) {
      console.log('[StoreStock] pickup_availability data:', data);
      var locations = data.pickup_availability_by_location_id
        ? Object.values(data.pickup_availability_by_location_id)
        : (Array.isArray(data) ? data : []);
      cache[variantId] = locations;
      render(locations);
    })
    .catch(function(err) {
      console.warn('[StoreStock] pickup_availability failed, trying storefront API:', err);

      // Fallback: use the storefront section rendering API
      // This renders a section and we parse inventory from the HTML
      fetch('/?section_id=store-stock-availability&variant_id=' + variantId)
        .then(function(res) { return res.text(); })
        .then(function() {
          // If both fail, try product JSON
          return fetchProductJson();
        })
        .catch(function() {
          return fetchProductJson();
        });
    });
  }

  function fetchProductJson() {
    const handle = getConfig().productHandle;
    const variantId = getConfig().currentVariantId;
    if (!handle) { showError(); return; }

    // /products/[handle].js returns total inventory but not per-location
    // We use it as a last resort to at least confirm overall availability
    fetch('/products/' + handle + '.js')
      .then(function(res) { return res.json(); })
      .then(function(product) {
        console.log('[StoreStock] product.js data:', product);
        const variant = product.variants.find(function(v) { return v.id === variantId; });
        if (!variant) { showError(); return; }

        // Show a single "Online stock" row as fallback
        const list = document.getElementById(LIST_ID);
        if (!list) return;

        const status = variant.available
          ? { label: 'In Stock', dotClass: 'store-stock-checker__dot--in-stock' }
          : { label: 'Out of Stock', dotClass: 'store-stock-checker__dot--out' };

        list.innerHTML = '<div class="store-stock-checker__row">' +
          '<span class="store-stock-checker__location">Stock available</span>' +
          '<span class="store-stock-checker__status">' +
            status.label +
            '<span class="store-stock-checker__dot ' + status.dotClass + '" aria-hidden="true"></span>' +
          '</span>' +
        '</div>' +
        '<p style="padding: 0.75rem; font-size: 0.8125rem; color: #888;">For specific store availability, please contact us.</p>';
      })
      .catch(function(err) {
        console.error('[StoreStock] All fetches failed:', err);
        showError();
      });
  }

  function getCurrentVariantTitle() {
    const checked = document.querySelectorAll('.product-form [aria-checked="true"]');
    if (checked.length) {
      return Array.from(checked).map(function(el) {
        return el.dataset.optionValue || el.textContent.trim();
      }).filter(Boolean).join(' / ');
    }
    const selects = document.querySelectorAll('.product-form select');
    if (selects.length) {
      return Array.from(selects).map(function(s) {
        return s.options[s.selectedIndex] ? s.options[s.selectedIndex].text.trim() : '';
      }).filter(Boolean).join(' / ');
    }
    return '';
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function updateVariant(newId) {
    if (window.storeStockConfig) window.storeStockConfig.currentVariantId = newId;
    const modal = getModal();
    if (modal && !modal.hasAttribute('hidden')) fetchAndRender();
  }

  function watchVariantChanges() {
    document.addEventListener('variant:changed', function(e) {
      if (e.detail && e.detail.variant) updateVariant(e.detail.variant.id);
    });
    document.addEventListener('change', function(e) {
      if (e.target && e.target.name === 'id' && e.target.closest('form')) {
        updateVariant(parseInt(e.target.value, 10));
      }
    });
    var lastParam = new URLSearchParams(window.location.search).get('variant');
    new MutationObserver(function() {
      var p = new URLSearchParams(window.location.search).get('variant');
      if (p && p !== lastParam) { lastParam = p; updateVariant(parseInt(p, 10)); }
    }).observe(document.querySelector('title') || document.body, { childList: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchVariantChanges);
  } else {
    watchVariantChanges();
  }

  return { open: open, close: close };
})();